import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import {
  FiChevronDown,
  FiChevronLeft,
  FiClock,
  FiRefreshCw,
  FiRotateCcw,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import {
  type CatalogItemOverrides,
  DesktopCatalogItem,
} from "@/features/catalog/components/desktop/DesktopCatalogItem";
import { HistoryModeToggle } from "@/features/history/components/HistoryModeToggle";
import {
  DESKTOP_HISTORY_ITEM_IMAGE_SIZE,
  DESKTOP_HISTORY_ITEM_TEXT_LINES,
  type DesktopHistorySidebarConfig,
  parseDesktopHistorySidebarConfig,
  resolveDesktopHistorySidebarContentState,
  selectDesktopHistoryItems,
  sortDesktopHistoryThreads,
} from "@/features/history/desktopHistorySidebar";
import { historyThreadKey } from "@/features/history/historyIdentity";
import {
  type HistoryMode,
  historyModeLabel,
} from "@/features/history/historyMode";
import {
  selectHistoryItemsForScope,
  useHistorySummaryScope,
} from "@/features/history/hooks/historySummaryScope";
import { useHistoryThreads } from "@/features/history/hooks/useHistoryThreads";
import { useHistoryStore } from "@/features/history/stores/historyStore";
import {
  selectActivePostedItems,
  usePostedHistoryStore,
} from "@/features/history/stores/postedHistoryStore";
import { useManualRefresh } from "@/shared/hooks/useManualRefresh";
import { resolveSafePersistStorage } from "@/shared/lib/safePersistStorage";
import { RefreshStatus } from "@/shared/ui/feedback";
import { Button } from "@/shared/ui/form";

function load(): DesktopHistorySidebarConfig {
  const storage = resolveSafePersistStorage() as Storage;
  return parseDesktopHistorySidebarConfig(storage.getItem("pc_sidebar_config"));
}

function save(config: DesktopHistorySidebarConfig): void {
  resolveSafePersistStorage().setItem(
    "pc_sidebar_config",
    JSON.stringify(config),
  );
}

/**
 * サイドバー表示は旧実装(本文2行固定・同一タブ遷移・未読バッジ常時表示)の
 * 挙動を維持する。カタログ共通設定(useCatalogStore)への連動は行わない。
 * クリック時のaddViewedは遷移先(DesktopThreadView)側で記録されるため、
 * サイドバー表示中に一覧の並びが書き換わらないよう無効化する。
 */
const SIDEBAR_ITEM_OVERRIDES: CatalogItemOverrides = {
  imageSize: DESKTOP_HISTORY_ITEM_IMAGE_SIZE,
  textLines: DESKTOP_HISTORY_ITEM_TEXT_LINES,
  openInNewTab: false,
  showCount: true,
  showUnreadCount: true,
  showFooter: false,
  disableViewedTracking: true,
};

export interface DesktopHistorySidebarHandle {
  toggle: () => void;
}

interface DesktopHistorySidebarProps {
  showClosedHandle?: boolean;
}

export const DesktopHistorySidebar: React.ForwardRefExoticComponent<
  DesktopHistorySidebarProps & React.RefAttributes<DesktopHistorySidebarHandle>
> = forwardRef(function DesktopHistorySidebar(
  { showClosedHandle = true }: DesktopHistorySidebarProps,
  ref,
): React.ReactNode {
  const viewedItems = useHistoryStore((state) => state.viewed);
  const postedItems = usePostedHistoryStore((state) => state.posted);
  const historyScope = useHistorySummaryScope();
  const viewed = selectDesktopHistoryItems(
    selectHistoryItemsForScope(viewedItems, historyScope),
  );
  const posted = selectDesktopHistoryItems(
    selectHistoryItemsForScope(
      selectActivePostedItems(postedItems),
      historyScope,
    ),
  );
  const clearViewed = useHistoryStore((state) => state.clearHistory);
  const removeViewed = useHistoryStore((state) => state.removeFromHistory);
  const clearPosted = usePostedHistoryStore(
    (state) => state.clearPostedHistory,
  );
  const removePosted = usePostedHistoryStore(
    (state) => state.removeFromPostedHistory,
  );
  const unread = useHistoryStore((s) => s.getUnreadCount);
  const [config, setConfig] = useState(load);
  const mode = config.historyMode;
  const historyItems = mode === "posted" ? posted : viewed;
  const query = useHistoryThreads(mode, historyItems, historyScope);
  const { isManualRefreshing, refreshManually } = useManualRefresh(
    query.refetch,
    query.isFetching,
  );
  const update = useCallback(
    (partial: Partial<DesktopHistorySidebarConfig>): void => {
      setConfig((current) => {
        const next = { ...current, ...partial };
        save(next);
        return next;
      });
    },
    [],
  );
  const toggle = useCallback((): void => {
    setConfig((current) => {
      const next = {
        ...current,
        sidebarVisible: !current.sidebarVisible,
      };
      save(next);
      return next;
    });
  }, []);

  useImperativeHandle(ref, () => ({ toggle }), [toggle]);
  const historyKeys = new Set(historyItems.map(historyThreadKey));
  const threads = sortDesktopHistoryThreads(
    (query.data ?? []).flatMap((thread) => {
      const item = {
        boardSlug: thread.b ?? historyScope.slugs[0],
        threadId: thread.id,
      };
      return item.boardSlug !== undefined &&
        historyKeys.has(historyThreadKey(item))
        ? [{ ...thread, ...item }]
        : [];
    }),
    historyItems,
    config.sortByNewRes,
    unread,
  );
  const contentState = resolveDesktopHistorySidebarContentState({
    historyCount: historyItems.length,
    isLoading: query.isLoading,
    hasError: query.error !== null,
    threadCount: threads.length,
  });
  const sidebar = !config.sidebarVisible ? (
    showClosedHandle ? (
      <Button
        variant="secondary"
        icon={<FiClock aria-hidden="true" size={16} />}
        className="fixed left-0 top-1/2 z-40 -translate-y-1/2 flex-col gap-1 rounded-l-none px-2 py-3 shadow-md"
        onClick={(): void => update({ sidebarVisible: true })}
        aria-label="履歴サイドバーを開く"
      >
        履歴
      </Button>
    ) : null
  ) : (
    <aside aria-label="PC履歴" className="desktop-history-sidebar">
      <header className="desktop-history-sidebar-heading">
        <h2>
          <FiRotateCcw aria-hidden="true" />
          履歴
        </h2>
        <button
          type="button"
          className="desktop-history-sidebar-close"
          onClick={(): void => update({ sidebarVisible: false })}
          aria-label="履歴サイドバーを閉じる"
        >
          <FiChevronLeft aria-hidden="true" />
        </button>
      </header>
      <HistoryModeToggle
        mode={mode}
        onChange={(historyMode: HistoryMode): void => update({ historyMode })}
        className="desktop-history-sidebar-mode-toggle"
      />
      <fieldset className="desktop-history-sidebar-controls">
        <legend className="sr-only">履歴の表示操作</legend>
        <label className="desktop-history-sidebar-sort">
          ソート:
          <select
            aria-label={`${historyModeLabel(mode)}の並び順`}
            value={config.sortByNewRes ? "newReplies" : "viewed"}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>): void =>
              update({
                sortByNewRes: event.currentTarget.value === "newReplies",
              })
            }
          >
            <option value="viewed">
              {mode === "posted" ? "書込順" : "閲覧順"}
            </option>
            <option value="newReplies">新着順</option>
          </select>
          <FiChevronDown aria-hidden="true" />
        </label>
        <button
          type="button"
          className="desktop-history-sidebar-refresh"
          disabled={query.isFetching}
          onClick={(): void => void refreshManually()}
        >
          <FiRefreshCw aria-hidden="true" />
          {query.isFetching ? "更新中" : "更新"}
        </button>
      </fieldset>
      <section aria-label="履歴項目" className="desktop-history-sidebar-scroll">
        {contentState === "empty" ? (
          <p className="desktop-history-sidebar-empty">
            {historyModeLabel(mode)}はありません
          </p>
        ) : contentState === "loading" ? (
          <p className="desktop-history-sidebar-empty">
            {historyModeLabel(mode)}を読み込み中...
          </p>
        ) : contentState === "error" ? (
          <p className="desktop-history-sidebar-empty">
            {historyModeLabel(mode)}の読み込みに失敗しました
          </p>
        ) : contentState === "noExistingThreads" ? (
          <p className="desktop-history-sidebar-empty">
            現在存在するスレッドはありません
          </p>
        ) : (
          <ol className="desktop-history-sidebar-list">
            {threads.map((thread) => (
              <li key={historyThreadKey(thread)}>
                <DesktopCatalogItem
                  thread={thread}
                  isNew={false}
                  overrides={SIDEBAR_ITEM_OVERRIDES}
                />
                <button
                  type="button"
                  className="desktop-history-sidebar-remove"
                  onClick={(): void =>
                    mode === "posted"
                      ? removePosted(thread.boardSlug, thread.threadId)
                      : removeViewed(thread.boardSlug, thread.threadId)
                  }
                  aria-label={`${historyModeLabel(mode)}から削除: ${thread.id}`}
                >
                  <FiX aria-hidden="true" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </section>
      <footer className="desktop-history-sidebar-footer">
        <button
          type="button"
          onClick={(): void =>
            mode === "posted"
              ? clearPosted(historyScope.slugs)
              : clearViewed(historyScope.slugs)
          }
          disabled={historyItems.length === 0}
        >
          <FiTrash2 aria-hidden="true" />
          履歴をすべて削除
        </button>
      </footer>
    </aside>
  );

  return (
    <>
      {sidebar}
      <RefreshStatus isRefreshing={isManualRefreshing} />
    </>
  );
});
